import './Input.scss';
import cn from 'classnames';
import { ReactComponent as Eye } from '../../icons/eye.svg';
import { ReactComponent as EyeCrossed } from '../../icons/eye-crossed.svg';
import { HTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button';

interface Props {
  value: string;
  onChange: React.ChangeEventHandler;
  onBlur?: React.ChangeEventHandler;
  placeholder?: string;
  type?: 'text' | 'email' | 'password';
  name?: string;
  id?: string;
  error?: string;
  touched?: boolean;
  className?: string;
  label?: string;
  as?: 'input' | 'textarea';
  rows?: number;
  disabled?: boolean;
}

const Input = ({
  value,
  onChange,
  placeholder,
  name,
  id,
  type = 'text',
  error,
  onBlur,
  touched,
  className = '',
  label,
  as = 'input',
  rows = 4,
  disabled = false,
  ...rest
}: Props & HTMLAttributes<HTMLInputElement | HTMLTextAreaElement>) => {
  const { t } = useTranslation();
  const isPassword = type === 'password';
  const [currentType, setCurrentType] = useState(type);

  const onEyeClick = () => {
    setCurrentType(currentType === 'password' ? 'text' : 'password');
  };

  return (
    <div className={`Input ${className}`}>
      {label && (
        <div className='Input__label mb-2' data-testid={`${name}-label`}>
          {t(label)}
        </div>
      )}
      {as === 'textarea' ? (
        <textarea
          id={id}
          name={name}
          data-testid={name}
          placeholder={placeholder ? t(placeholder) : undefined}
          onChange={onChange}
          value={value}
          rows={rows}
          onBlur={onBlur}
          className={cn('Input__input', {
            'Input__input--invalid': touched && !!error,
          })}
          disabled={disabled}
          {...rest}
        />
      ) : (
        <input
          {...rest}
          id={id}
          name={name}
          data-testid={name}
          type={currentType}
          placeholder={placeholder ? t(placeholder) : undefined}
          onChange={onChange}
          value={value}
          disabled={disabled}
          onBlur={onBlur}
          className={cn('Input__input', {
            'Input__input--invalid': touched && !!error,
            'Input__input--withEye': isPassword,
          })}
          autoComplete='on'
        />
      )}
      {isPassword && (
        <Button
          styling='naked'
          type='button'
          className='Input__eye position-absolute'
          onClick={onEyeClick}
          tabIndex={-1}
          data-testid={name + '-eye'}
        >
          {currentType === 'password' ? <Eye /> : <EyeCrossed />}
        </Button>
      )}
      {touched && error && (
        <div className='Input__error' data-testid={`${name}-error`}>
          {t(error)}
        </div>
      )}
    </div>
  );
};

export default Input;
